'use client';

import { useState } from 'react';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const interestedOptions = [
  'NEV Spare Parts Wholesale',
  'Technical Training & Escort',
  'EV Battery Repair Program',
  'Distributor / Agent Partnership',
  'Garage Transformation',
  'Other Inquiry',
];

type Status = 'idle' | 'submitting' | 'success' | 'error';

export function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('/api/contact/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: formData.get('companyName'),
          city: formData.get('city'),
          contactPerson: formData.get('contactPerson'),
          email: formData.get('email'),
          whatsapp: formData.get('whatsapp') || '',
          estimatedVolume: formData.get('estimatedVolume') || '',
          interestedIn: formData.get('interestedIn'),
          requirements: formData.get('requirements'),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Submission failed');
      }

      setStatus('success');
      form.reset();
    } catch (error) {
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong');
    }
  };

  if (status === 'success') {
    return (
      <div className="p-8 md:p-10 rounded-2xl bg-[var(--color-surface)] border border-[var(--color-border)]">
        <div className="text-center py-12">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/10 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-[var(--color-primary)]" />
          </div>
          <h3 className="text-2xl font-bold mb-3">Message Sent!</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            Thank you for reaching out. Our team has received your inquiry and will get back to you within 24 business hours.
          </p>
          <Button
            onClick={() => setStatus('idle')}
            variant="outline"
            className="mt-8"
          >
            Send Another Message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 md:p-10 rounded-2xl bg-[var(--color-surface)] border border-[var(--color-border)]">
      <h3 className="text-xl font-bold mb-2">Send Us an Inquiry</h3>
      <p className="text-sm text-muted-foreground mb-7">
        Fill out the form and our team will get back to you within 24 business hours.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-2">
            <label className="text-sm font-medium">Company Name <span className="text-[var(--color-primary)]">*</span></label>
            <Input
              name="companyName"
              required
              placeholder="Your company name"
              disabled={status === 'submitting'}
              className="h-12 bg-background border-[var(--color-border)] focus:border-[var(--color-primary)] rounded-lg"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">City / Country <span className="text-[var(--color-primary)]">*</span></label>
            <Input
              name="city"
              required
              placeholder="e.g. Dubai, UAE"
              disabled={status === 'submitting'}
              className="h-12 bg-background border-[var(--color-border)] focus:border-[var(--color-primary)] rounded-lg"
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-2">
            <label className="text-sm font-medium">Contact Person <span className="text-[var(--color-primary)]">*</span></label>
            <Input
              name="contactPerson"
              required
              placeholder="Your full name"
              disabled={status === 'submitting'}
              className="h-12 bg-background border-[var(--color-border)] focus:border-[var(--color-primary)] rounded-lg"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Email <span className="text-[var(--color-primary)]">*</span></label>
            <Input
              name="email"
              type="email"
              required
              placeholder="business@company.com"
              disabled={status === 'submitting'}
              className="h-12 bg-background border-[var(--color-border)] focus:border-[var(--color-primary)] rounded-lg"
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-2">
            <label className="text-sm font-medium">WhatsApp (optional)</label>
            <Input
              name="whatsapp"
              placeholder="+country code"
              disabled={status === 'submitting'}
              className="h-12 bg-background border-[var(--color-border)] focus:border-[var(--color-primary)] rounded-lg"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Estimated Annual Volume</label>
            <div className="relative">
              <select
                name="estimatedVolume"
                disabled={status === 'submitting'}
                className="w-full h-12 px-4 bg-background border border-[var(--color-border)] focus:border-[var(--color-primary)] rounded-lg appearance-none cursor-pointer pr-10"
              >
                <option value="">Select range</option>
                <option value="under50k">Under $50,000</option>
                <option value="50k-200k">$50,000 - $200,000</option>
                <option value="200k-500k">$200,000 - $500,000</option>
                <option value="500k-1m">$500,000 - $1,000,000</option>
                <option value="over1m">Over $1,000,000</option>
              </select>
              <svg
                className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none"
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Interested In <span className="text-[var(--color-primary)]">*</span></label>
          <div className="relative">
            <select
              name="interestedIn"
              required
              disabled={status === 'submitting'}
              className="w-full h-12 px-4 bg-background border border-[var(--color-border)] focus:border-[var(--color-primary)] rounded-lg appearance-none cursor-pointer pr-10"
            >
              <option value="">Select one or more</option>
              {interestedOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none"
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Your Requirements <span className="text-[var(--color-primary)]">*</span></label>
          <Textarea
            name="requirements"
            required
            placeholder="Tell us about your business, current situation and how we can help you..."
            rows={5}
            disabled={status === 'submitting'}
            className="bg-background border-[var(--color-border)] focus:border-[var(--color-primary)] rounded-lg resize-none"
          />
        </div>

        {status === 'error' && (
          <div className="flex items-center gap-2 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-sm text-red-300">{errorMessage || 'Failed to send message. Please try again.'}</p>
          </div>
        )}

        <Button
          type="submit"
          size="lg"
          disabled={status === 'submitting'}
          className="w-full rounded-xl shadow-[0_0_30px_rgba(0,195,122,0.4)]"
        >
          <Send className="mr-2 w-4 h-4" />
          {status === 'submitting' ? 'Sending...' : 'Send Inquiry'}
        </Button>
      </form>
    </div>
  );
}
